// Funções auxiliares para controle de permissões do novo nível de usuário

/**
 * Calcula o nível hierárquico baseado na ordem
 * @param {string} ordem - A ordem da atividade (ex: "1.2.3")
 * @returns {number} - O nível (0 para "1", 1 para "1.2", 2 para "1.2.3")
 */
export const calculateLevel = (ordem) => {
  if (!ordem) return 0
  const cleanOrder = ordem.replace(/\.$/, '')
  const parts = cleanOrder.split('.')
  return parts.length - 1
}

/**
 * Verifica se o usuário tem o novo nível de permissão
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const hasSpecialLevelUtil = (authStore) => {
  return authStore.getUserData().nivel === true
}

/**
 * Verifica se o usuário é proprietário da atividade
 * @param {Object} activity - Atividade
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const isOwner = (activity, authStore) => {
  if (!activity) return false
  const currentUserId = authStore.getUserData()?.id || localStorage.getItem('rn573_user_id')
  return activity.autor_id === currentUserId || activity.criado_por === currentUserId
}

/**
 * Verifica se o usuário é responsável pela atividade
 * @param {Object} activity - Atividade
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const isResponsible = (activity, authStore) => {
  if (!activity || !activity.responsaveis) return false
  const currentUserId = authStore.getUserData()?.id || localStorage.getItem('rn573_user_id')
  return activity.responsaveis.some(r => r.id == currentUserId)
}

/**
 * Verifica se o usuário pode editar uma atividade
 * @param {Object} activity - Atividade
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const canEditActivityUtil = (activity, authStore) => {
  // Administrador sempre pode editar
  if (authStore.isAdmin()) return true
  // Se não tem nível especial, só pode ver
  if (!hasSpecialLevelUtil(authStore)) return false

  const level = calculateLevel(activity.ordem || activity.order)

  // Pode editar se for o autor da atividade
  // if (isOwner(activity, authStore) && level === 2) {
  //   return true
  // }

  // // Pode editar se for responsável por uma subtarefa de nível 3 (level 2)
  // if (level === 2 && isResponsible(activity, authStore)) {
  //   return true
  // }

  if (level === 2) return true

  return false
}

/**
 * Verifica se o usuário pode criar uma subtarefa para a atividade pai
 * @param {Object} parentActivity - Atividade pai
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const canCreateSubtaskUtil = (parentActivity, authStore) => {
  // Administrador sempre pode
  if (authStore.isAdmin()) return true
  // Se não tem nível especial, não pode
  if (!hasSpecialLevelUtil(authStore)) return false

  const level = calculateLevel(parentActivity.ordem || parentActivity.order)

  // Usuário com nível especial só pode criar subtarefas de nível 3 (o pai deve ser nível 2)
  return level === 1
}

/**
 * Verifica se o usuário pode editar a ordenação da atividade
 * @param {Object} activity - Atividade
 * @param {Object} authStore - Store de autenticação
 * @returns {boolean}
 */
export const canEditOrderUtil = (activity, authStore) => {
  if (authStore.isAdmin()) return true
  if (!hasSpecialLevelUtil(authStore)) return false

  const level = calculateLevel(activity.ordem || activity.order)

  // Usuário com nível especial só pode editar a ordenação se for responsável por subtarefas de nível 3 (level 2)
  return level === 2 && isResponsible(activity, authStore)
}

/**
 * Obtém mensagem de permissão para edição/visualização
 * @param {Object} activity - Atividade
 * @param {Object} authStore - Store de autenticação
 * @param {string|number} parentId - ID do pai
 * @returns {string}
 */
export const getPermissionMessageUtil = (activity, authStore, parentId = null) => {
  if (!hasSpecialLevelUtil(authStore)) return ''

  if (!activity) {
    // Criando nova atividade
    if (parentId) {
      const parentLevel = calculateLevel(parentId)
      if (parentLevel === 1) {
        return 'Você pode criar apenas subtarefas de nível 3 (ex: 1.1.1)'
      }
      return 'Você só pode criar subtarefas a partir de atividades de nível 2'
    }
    return 'Você não pode criar atividades principais'
  }

  // Editando atividade existente
  const level = calculateLevel(activity.ordem || activity.order)

  if (isOwner(activity, authStore)) {
    return 'Você pode editar esta atividade (proprietário)'
  }

  if (level === 2 && isResponsible(activity, authStore)) {
    return 'Você pode editar esta subtarefa (responsável)'
  }

  return 'Você não tem permissão para editar esta atividade'
}

/**
 * Obtém mensagem para adicionar subtarefa
 * @param {Object} activity - Atividade pai
 * @param {Object} authStore - Store de autenticação
 * @returns {string}
 */
export const getAddSubtaskMessageUtil = (activity, authStore) => {
  if (!hasSpecialLevelUtil(authStore) || authStore.isAdmin()) {
    return 'Criar uma nova subtarefa'
  }

  const level = calculateLevel(activity.ordem || activity.order)

  if (level === 1) {
    return 'Criar Subtarefa'
  }

  return 'Não é permitido criar subtarefa'
}
